import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Scissors } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FirmSelect from "@/components/FirmSelect";
import DeductionForm from "@/components/DeductionForm";
import { fmtTL, fmtDate } from "@/lib/utils";

export default function Deductions() {
  const [rows, setRows] = useState([]);
  const [firms, setFirms] = useState([]);
  const [firm, setFirm] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (firm) params.firm_id = firm;
    if (status !== "all") params.status = status;
    api.get("/deductions", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { api.get("/firms").then(r => setFirms(r.data)); }, []);
  useEffect(() => { load(); }, [firm, status]);

  const remove = async (d) => {
    if (!window.confirm("Kesinti silinsin mi?")) return;
    try { await api.delete(`/deductions/${d.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const pending = rows.filter(r => r.status === "bekliyor").reduce((s, r) => s + r.amount, 0);
  const applied = rows.filter(r => r.status === "uygulandi").reduce((s, r) => s + r.amount, 0);

  return (
    <div data-testid="deductions-page">
      <PageHeader title="Hakediş Kesintileri" subtitle="Firmalara ait kesintileri girin; Hakediş sayfasında seçerek hakedişten düşün.">
        <FirmSelect firms={firms} value={firm} onChange={setFirm} testid="ded-filter-firm" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="ded-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tümü</SelectItem><SelectItem value="bekliyor">Bekleyen</SelectItem><SelectItem value="uygulandi">Uygulanan</SelectItem></SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-deduction-button"><Plus className="w-4 h-4 mr-1" /> Yeni Kesinti</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Yeni Kesinti</DialogTitle></DialogHeader>
            <DeductionForm firms={firms} onCreated={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </PageHeader>
      <div className="flex gap-6 mb-4 text-sm" data-testid="ded-summary">
        <span><span className="text-muted-foreground">Bekleyen:</span> <strong className="font-mono-nums text-amber-700 dark:text-amber-400">{fmtTL(pending)}</strong></span>
        <span><span className="text-muted-foreground">Hakedişe uygulanan:</span> <strong className="font-mono-nums">{fmtTL(applied)}</strong></span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <Scissors className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Kesinti kaydı yok</div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>Firma</TableHead><TableHead>Grup</TableHead><TableHead>Kesinti</TableHead><TableHead>Dönem</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead>Durum</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{rows.map(d => (
              <TableRow key={d.id} data-testid={`ded-row-${d.id}`}>
                <TableCell className="text-sm">{d.firm_name || "-"}</TableCell>
                <TableCell className="text-xs font-semibold">{d.category}</TableCell>
                <TableCell className="text-sm">{d.name}{d.notes && <div className="text-xs text-muted-foreground">{d.notes}</div>}</TableCell>
                <TableCell className="text-xs">{fmtDate(d.period)}</TableCell>
                <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(d.amount)}</TableCell>
                <TableCell><Badge className={d.status === "bekliyor" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"}>{d.status === "bekliyor" ? "Bekliyor" : "Uygulandı"}</Badge>
                  {d.payment_label && <div className="text-[11px] text-muted-foreground mt-0.5">Hakediş {d.payment_label.split(" · ")[0]} · {fmtDate(d.payment_label.split(" · ")[1])}</div>}</TableCell>
                <TableCell className="text-right">{d.status === "bekliyor" && <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`ded-delete-${d.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
